import {
    AfterContentInit,
    Component,
    ContentChild,
    EventEmitter,
    HostBinding,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    TemplateRef,
} from "@angular/core";
import { HexDataListLayout } from "@hex/angular/data-list";
import { BooleanAttribute } from "@hex/angular/decorators";
import { Observable ,  Subscription } from "rxjs";
import { Role } from "../../../auth/roles";
import { UserRoleService } from "../../../auth/user-role-service";
import { ImmutableRedux } from "../../../redux/immutable-redux.service";
import { RowSelectionManagerDirective } from "../../row-selection/manager.directive";
import { CommonListActionsDirective } from "../actions.directive";
import { ClickRowService } from "../click-row.service";
import { DATALIST_STATE_PROP } from "../state-props";
import { IDataListState, IDynamicDataListColumn } from "./common-datalist.interface";
import { commonDatalistReducerFactory } from "./reducer";

@Component({
    selector: "common-datalist",
    templateUrl: "./common-datalist.component.html",
    styleUrls: ["./common-datalist.component.scss"],
    providers: [
        HexDataListLayout,
    ],
})
export class CommonDatalistComponent implements OnInit, AfterContentInit, OnDestroy {
    @HostBinding("class") public classes = "u-flex-grow-1 u-display-block";
    @Input() @HostBinding("style.width") public containerWidth: string;
    @Input() @HostBinding("class.c-datalist__row--top-aligned") public topAlignRows = false;
    @Input() public initialState: IDataListState;
    @Input() public stateProp: string;
    @Input() @BooleanAttribute() public alternatingRowStyles = false;
    @Input() @BooleanAttribute() public darkBackground = false;
    @Input() public isExpandable = false;
    @Input() public bulkSelectManager: RowSelectionManagerDirective;
    @Input() @BooleanAttribute() public keepStateAliveOnDestroy = false;
    @Input() public expandedRowHeight = 1000;
    @Input() public listIsReadOnly = false;
    @Input() public paramId: number;
    @Input() filter: string;
    @Input("containerHeight") public set containerHeightInputSetter(value: number) {
        if (value) {
            this.containerHeightInputValue = value;
            this.containerHeight = value;
        }
    }

    @Output() public numberOfExpandedRowsChanged: EventEmitter<any> = new EventEmitter();

    @ContentChild("datalistRow", {static: false}) public datalistRow: TemplateRef<any>;
    @ContentChild("expandedDetails", {static: false}) public expandedDetails: TemplateRef<any>;

    public datalist$: Observable<any>;
    public datalistSubscription: Subscription;
    public results: any[] = [];
    public columns: IDynamicDataListColumn[];
    public selectedId: number;
    public isExhausted = false;
    public initialized = false;
    public expandedRows = {};
    public numberOfExpandedRows = 0;
    public containerHeightInputValue: number;
    public containerHeight: number;

    constructor(
        private ngRedux: ImmutableRedux<any>,
        public listActions: CommonListActionsDirective,
        @Optional() public clickRowService: ClickRowService,
        public userRoleService: UserRoleService,
        @Inject(DATALIST_STATE_PROP) public rootProp,
    ) {
        listActions.rootProp = this.rootProp;
        listActions.isExpandable = this.isExpandable;
    }

    public ngOnInit(): void {
        if (!this.keepStateAliveOnDestroy || !this.ngRedux.checkState(this.rootProp, this.stateProp)) {
            this.ngRedux.registerComponent(commonDatalistReducerFactory, this.rootProp, this.stateProp, this.initialState);
        }
    }

    public ngAfterContentInit(): void {
        if (this.clickRowService.init) { this.clickRowService.init(); }
        this.datalist$ = this.ngRedux.select(this.rootProp, this.stateProp);
        this.datalistSubscription = this.datalist$.subscribe((state: any) => {
            if (state !== undefined) {
                this.initialized = true;
                this.columns = state.listData.columns;
                this.results = state.listData.results;
                if (this.filter) {
                    this.results = state.listData.results.filter((result) => result.versionType === this.filter);
                }
                this.selectedId = state.listData.selectedId;
                this.isExhausted = state.listData.isExhausted;
                this.expandedRows = state.listData.expandedRows;
                if (this.numberOfExpandedRows !== state.listData.numberOfExpandedRows) {
                    this.numberOfExpandedRows = state.listData.numberOfExpandedRows;
                    if (this.containerHeightInputValue) {
                        this.numberOfExpandedRows > 0 ? this.containerHeight = this.expandedRowHeight : this.containerHeight = this.containerHeightInputValue;
                    }
                }
            }
        });
    }

    public ngOnDestroy() {
        this.resetListIfNeeded();
        if (this.datalist$) { this.datalistSubscription.unsubscribe(); }
        if (this.clickRowService.destroy) { this.clickRowService.destroy(); }
    }

    public isExpanded(index): boolean {
        return this.expandedRows ? this.expandedRows[index] : false;
    }

    public getSortCaret(col): string {
        return col.sortDirection > 0 ? "small-caret-up" : "small-caret-down";
    }

    public selectRow(row, index) {
        if (this.clickRowService) {
            this.clickRowService.clickRow(row, index, {
                rootProp: this.rootProp,
                stateProp: this.stateProp,
            });
        }
    }

    public alternateRow(index) {
        const alternate = !!(index % 2) && this.alternatingRowStyles;
        return alternate;
    }

    public resetListIfNeeded() {
        if (!this.keepStateAliveOnDestroy) {
            this.listActions.resetList();
        }
    }

    public showColumn(col) {
        if (col != null) {
            return !(col.hideOnReadOnly && this.listIsReadOnly);
        } else {
            return true;
        }
    }
}
